import { useState, type FormEvent } from 'react';
import { DEFAULT_BANDS, SCORE_TYPES, type Assessment, type Band, type Measure } from '@td/shared';
import { useCreateAssessment, useUpdateAssessment } from '../hooks/useAssessments';
import { useConfirm } from './ConfirmDialog';
import { ApiRequestError } from '../lib/api';
import { Modal } from './Modal';

const TYPE_LABELS: Record<Measure['scoreType'], string> = {
  number: 'A number',
  percent: 'So many out of a total',
  level: 'A level, typed in',
};

function freshMeasure(index: number): Measure {
  return {
    id: `m-${Date.now().toString(36)}-${index}`,
    name: '',
    scoreType: 'number',
    outOf: null,
    unit: '',
    bands: [],
  };
}

/** Build or edit an assessment: its measures, units, and benchmark bands. */
export function AssessmentEditor({
  assessment,
  onClose,
}: {
  assessment: Assessment | null;
  onClose: () => void;
}) {
  const create = useCreateAssessment();
  const update = useUpdateAssessment();
  const confirm = useConfirm();

  const [name, setName] = useState(assessment?.name ?? '');
  const [subject, setSubject] = useState(assessment?.subject ?? '');
  const [termly, setTermly] = useState(assessment?.termly ?? true);
  const [measures, setMeasures] = useState<Measure[]>(assessment?.measures ?? [freshMeasure(0)]);
  const [error, setError] = useState<string | null>(null);

  function patchMeasure(id: string, patch: Partial<Measure>) {
    setMeasures((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }

  function patchBand(measureId: string, index: number, patch: Partial<Band>) {
    setMeasures((ms) =>
      ms.map((m) =>
        m.id === measureId
          ? { ...m, bands: m.bands.map((b, i) => (i === index ? { ...b, ...patch } : b)) }
          : m,
      ),
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cleaned = measures
      .filter((m) => m.name.trim())
      .map((m) => ({
        ...m,
        name: m.name.trim(),
        bands: [...m.bands].sort((a, b) => a.min - b.min),
      }));

    if (cleaned.length === 0) {
      setError('Give at least one measure a name.');
      return;
    }

    try {
      if (assessment) {
        await update.mutateAsync({ id: assessment.id, name, subject, termly, measures: cleaned });
      } else {
        await create.mutateAsync({ name, subject, termly, measures: cleaned });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That could not be saved.');
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Assessment"
      className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-4 text-lg">{assessment ? 'Edit assessment' : 'New assessment'}</h2>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="a-name">
              Name
            </label>
            <input
              id="a-name"
              className="field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Winter unit check"
              required
              maxLength={80}
              autoFocus
            />
          </div>
          <div>
            <label className="label" htmlFor="a-subject">
              Subject
            </label>
            <input
              id="a-subject"
              className="field"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Reading"
              maxLength={40}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setTermly(true)}
            aria-pressed={termly}
            className={termly ? 'btn-primary py-1.5 text-xs' : 'btn-soft'}
          >
            Given at BOY / MOY / EOY
          </button>
          <button
            type="button"
            onClick={() => setTermly(false)}
            aria-pressed={!termly}
            className={!termly ? 'btn-primary py-1.5 text-xs' : 'btn-soft'}
          >
            Given on a date
          </button>
        </div>

        <div>
          <p className="label">Measures</p>
          <ul className="space-y-3">
            {measures.map((m) => (
              <li key={m.id} className="rounded-card border border-sand p-3">
                <div className="mb-2 flex flex-wrap items-end gap-2">
                  <div className="min-w-[160px] flex-1">
                    <span className="label mb-1 text-xs">Name</span>
                    <input
                      className="field py-1.5"
                      value={m.name}
                      maxLength={60}
                      placeholder="Words correct per minute"
                      onChange={(e) => patchMeasure(m.id, { name: e.target.value })}
                    />
                  </div>

                  <div className="w-24">
                    <span className="label mb-1 text-xs">Unit</span>
                    <input
                      className="field py-1.5"
                      value={m.unit}
                      maxLength={20}
                      placeholder="wcpm"
                      onChange={(e) => patchMeasure(m.id, { unit: e.target.value })}
                    />
                  </div>

                  <div>
                    <span className="label mb-1 text-xs">Scored as</span>
                    <select
                      className="field w-auto py-1.5"
                      value={m.scoreType}
                      onChange={(e) =>
                        patchMeasure(m.id, {
                          scoreType: e.target.value as Measure['scoreType'],
                          outOf: e.target.value === 'percent' ? (m.outOf ?? 20) : null,
                        })
                      }
                    >
                      {SCORE_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {TYPE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                  </div>

                  {m.scoreType === 'percent' && (
                    <div className="w-20">
                      <span className="label mb-1 text-xs">Out of</span>
                      <input
                        className="field py-1.5"
                        type="number"
                        min={1}
                        value={m.outOf ?? 20}
                        onChange={(e) => patchMeasure(m.id, { outOf: Number(e.target.value) || 1 })}
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setMeasures((ms) => ms.filter((x) => x.id !== m.id))}
                    className="btn-soft-danger"
                    disabled={measures.length === 1}
                  >
                    Remove
                  </button>
                </div>

                <div className="rounded-lg bg-linen/50 p-2">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[11px] text-walnut">Benchmark bands</span>
                    {m.bands.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => patchMeasure(m.id, { bands: DEFAULT_BANDS })}
                        className="btn-soft-accent"
                      >
                        Add bands
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => patchMeasure(m.id, { bands: [] })}
                        className="btn-soft"
                      >
                        No bands
                      </button>
                    )}
                  </div>

                  {m.bands.length === 0 ? (
                    <p className="text-[11px] leading-snug text-stone">
                      Unbanded — scores show as plain numbers. Add bands and cells colour
                      themselves; put in the cut scores from your own materials.
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {m.bands.map((band, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <input
                            type="color"
                            value={band.color}
                            onChange={(e) => patchBand(m.id, i, { color: e.target.value })}
                            className="h-6 w-8 cursor-pointer rounded border border-sand bg-white"
                            aria-label={`Colour for ${band.label}`}
                          />
                          <input
                            className="field py-1 text-xs"
                            value={band.label}
                            maxLength={30}
                            onChange={(e) => patchBand(m.id, i, { label: e.target.value })}
                            aria-label="Band name"
                          />
                          <span className="shrink-0 text-[11px] text-stone">from</span>
                          <input
                            className="field w-20 py-1 text-xs"
                            type="number"
                            value={band.min}
                            onChange={(e) => patchBand(m.id, i, { min: Number(e.target.value) })}
                            aria-label="Lowest score in this band"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              patchMeasure(m.id, { bands: m.bands.filter((_, x) => x !== i) })
                            }
                            className="btn-soft-danger shrink-0"
                            aria-label={`Remove ${band.label}`}
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                      <li>
                        <button
                          type="button"
                          onClick={() =>
                            patchMeasure(m.id, {
                              bands: [...m.bands, { label: 'New band', min: 0, color: '#C4A287' }],
                            })
                          }
                          className="btn-soft mt-1"
                        >
                          + Another band
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => setMeasures((ms) => [...ms, freshMeasure(ms.length)])}
            className="btn-soft-accent mt-2"
          >
            + Add a measure
          </button>
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={async () => {
              const dirty = name.trim() || measures.some((m) => m.name.trim());
              if (
                dirty &&
                !(await confirm({
                  title: 'Discard this assessment?',
                  body: 'Nothing you have typed here will be kept.',
                  confirmLabel: 'Discard',
                  danger: true,
                }))
              )
                return;
              onClose();
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending || update.isPending}
          >
            {create.isPending || update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
